import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import config from "../config";
import keycloak from '../keycloak'
import {
  isObjectStorageServiceInactiveError,
  OBJECT_STORAGE_SERVICE_REQUIRED_EVENT,
} from '../utils/object-storage-service'
import {
  isDedicatedServiceInactiveError,
  DEDICATED_SERVICE_REQUIRED_EVENT,
} from '../utils/dedicated-service'
// import Config from "../Config";
const ApiClient = axios.create({
  baseURL: `${config.apiUrl}api/`,
  // baseURL: 'https://core.ocplanet.cloud/backend/api/',
  withCredentials: true,

});

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean,
}

let tokenRefreshPromise: Promise<boolean> | null = null

export const refreshAccessToken = async (minValidity = 60): Promise<boolean> => {
  if (!keycloak?.authenticated) {
    return false
  }

  if (!tokenRefreshPromise) {
    tokenRefreshPromise = keycloak
      .updateToken(minValidity)
      .then(() => Boolean(keycloak.token))
      .catch(() => false)
      .finally(() => {
        tokenRefreshPromise = null
      })
  }

  return tokenRefreshPromise
}

const applyRequestHeaders = (requestConfig: InternalAxiosRequestConfig, token?: string | null) => {
  requestConfig.headers = requestConfig.headers || {}

  requestConfig.headers['Accept-Language'] = `${localStorage.getItem('lang') || 'en'}`

  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`
  }

  return requestConfig
}

ApiClient.interceptors.response.use(response => {
  return response;
}, async (error: AxiosError<any>) => {
  const requestConfig = error.config as RetryableRequestConfig | undefined
  const status = error?.response?.status
  const detail = error?.response?.data?.detail
  const expiredToken = detail === 'Expired token.'
  const shouldRetry = Boolean(requestConfig) && !requestConfig?._retry && (status === 401 || status === 498 || expiredToken)

  if (shouldRetry && requestConfig) {
    requestConfig._retry = true

    const refreshed = await refreshAccessToken(0)

    if (refreshed && keycloak.token) {
      applyRequestHeaders(requestConfig, keycloak.token)
      return ApiClient(requestConfig)
    }
  }

  if (isObjectStorageServiceInactiveError(error) && window.location.pathname.startsWith('/object-storage')) {
    window.dispatchEvent(new Event(OBJECT_STORAGE_SERVICE_REQUIRED_EVENT))
  }

  if (
    isDedicatedServiceInactiveError(error)
    && requestConfig?.url?.includes('dedicated-servers/')
    && window.location.pathname.startsWith('/bare-metal/')
  ) {
    window.dispatchEvent(new Event(DEDICATED_SERVICE_REQUIRED_EVENT))
  }

  return Promise.reject(error);
});

ApiClient.interceptors.request.use(
  async config => {
    if (keycloak?.authenticated) {
      await refreshAccessToken(60)
    }

    return applyRequestHeaders(config, keycloak?.token);
  },
  error => Promise.reject(error)
);
export const setClientToken = (token:any) => {
  if (token) {
    ApiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
    // ApiClient.defaults.withCredentials = true
  }
};


export const resetClientToken = () => {
  delete ApiClient.defaults.headers.common.Authorization;
};

export default ApiClient
